import { AppType, ApiResponse, User, VocabDashboardData, HallouminatiDashboardData, ProfileData, ReminderFrequency, TopicMasteryData } from '@/types';
import { coreApiClient, ApiError } from './core';

export const userApi = {
    async getVocabData(appType: AppType): Promise<ApiResponse<VocabDashboardData>> {
        const response = await coreApiClient.axios.get<ApiResponse<VocabDashboardData>>(
            `/api/${appType}/vocab`
        );

        if (response.data.success && response.data.data) {
            const data = response.data.data;
            const wordsPracticed = data.vocab_stats?.words_today || 0;
            const lastPracticeTimestamp = data.stats?.overall?.last_practiced_at
                ? new Date(data.stats.overall.last_practiced_at).getTime()
                : 0;

            import('@/services/widgetService').then(({ WidgetService }) => {
                WidgetService.updateWidgetData({
                    wordsPracticed,
                    lastPracticeTimestamp
                });
            });
        }

        return response.data;
    },

    async getHallouminatiDashboard(): Promise<ApiResponse<HallouminatiDashboardData>> {
        const response = await coreApiClient.axios.get<ApiResponse<HallouminatiDashboardData>>(
            '/api/hallouminati/dashboard'
        );
        return response.data;
    },

    async getTopicMastery(topic: string): Promise<ApiResponse<TopicMasteryData>> {
        const response = await coreApiClient.axios.get<ApiResponse<TopicMasteryData>>(
            '/api/hallouminati/topic-mastery',
            { params: { topic } }
        );
        return response.data;
    },

    async getProfileData(appType: AppType): Promise<ApiResponse<ProfileData>> {
        try {
            const response = await coreApiClient.axios.get<ApiResponse<ProfileData>>(
                '/api/profile',
                { params: { app_type: appType },
                    headers: {
                        'X-Telegram-Init-Data': window.Telegram?.WebApp?.initData || undefined
                    }
                }
            );
            return response.data;
        } catch (error) {
            // Preserve ApiError for proper error type detection in Auth
            if (error instanceof ApiError) {
                return {
                    success: false,
                    error: error.message,
                    _apiError: error // Preserve the original error for type checking
                } as ApiResponse<ProfileData> & { _apiError: ApiError };
            }
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to load profile'
            };
        }
    },

    async updateSettings(
        appType: AppType,
        settings: { locale?: string; quiz_language?: string; reminder_frequency?: ReminderFrequency }
    ): Promise<ApiResponse<void>> {
        try {
            const response = await coreApiClient.axios.post<ApiResponse<void>>(
                '/api/profile/settings',
                settings,
                { params: { app_type: appType } }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to update settings'
            };
        }
    },

    async getDashboardData(appType: AppType): Promise<ApiResponse<HallouminatiDashboardData | (VocabDashboardData & { user: User | null })>> {
        if (appType === 'hallouminati') {
            return this.getHallouminatiDashboard();
        } else {
            // The vocab dashboard carries `user` too, null for guests (VocabController::getVocabDashboard).
            return this.getVocabData(appType) as Promise<ApiResponse<VocabDashboardData & { user: User | null }>>;
        }
    },

    async resetProgress(appType: AppType): Promise<ApiResponse<void>> {
        try {
            const response = await coreApiClient.axios.post<ApiResponse<void>>(
                '/api/profile/reset-progress',
                { app_type: appType }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to reset progress'
            };
        }
    },

    async deleteAccount(): Promise<ApiResponse<void>> {
        try {
            const response = await coreApiClient.axios.delete<ApiResponse<void>>('/api/auth/account');
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to delete account'
            };
        }
    }
};
